import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Laptop, Users, Building2, Search } from 'lucide-react';
import { FORM_CATEGORIES, type FormType } from '../../../../shared/form-types';
import { useTranslation } from 'react-i18next';

const ICONS = {
  Laptop,
  Users,
  Building2,
};

export function SelectTicketTypePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCategories = FORM_CATEGORIES.map((category) => ({
    ...category,
    forms: category.forms.filter((form) => {
      const name = t(`tickets.forms.${form.id}.name`).toLowerCase();
      const desc = t(`tickets.forms.${form.id}.description`).toLowerCase();
      return name.includes(searchQuery.toLowerCase()) ||
             desc.includes(searchQuery.toLowerCase());
    }),
  })).filter((category) => category.forms.length > 0);

  const handleSelectForm = (formType: FormType) => {
    navigate(`/tickets/new/${formType}`);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityLabel = (priority: string) => {
    return t(`tickets.select.priority.${priority}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{t('tickets.select.title')}</h1>
        <p className="text-muted-foreground">{t('tickets.select.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t('tickets.select.search_placeholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Categories */}
      <div className="space-y-8">
        {filteredCategories.map((category) => {
          const IconComponent = ICONS[category.icon as keyof typeof ICONS];

          return (
            <div key={category.id} className="space-y-4">
              <div className="flex items-center gap-3">
                {IconComponent && <IconComponent className="h-6 w-6 text-primary" />}
                <div>
                  <h2 className="text-xl font-semibold">{t(`tickets.categories.${category.id}.name`)}</h2>
                  <p className="text-sm text-muted-foreground">{t(`tickets.categories.${category.id}.description`)}</p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {category.forms.map((form) => (
                  <Card
                    key={form.id}
                    className="cursor-pointer transition-all hover:shadow-md hover:border-primary"
                    onClick={() => handleSelectForm(form.id)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base">{t(`tickets.forms.${form.id}.name`)}</CardTitle>
                        <Badge className={getPriorityColor(form.priority)} variant="secondary">
                          {getPriorityLabel(form.priority)}
                        </Badge>
                      </div>
                      <CardDescription className="text-sm">{t(`tickets.forms.${form.id}.description`)}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {filteredCategories.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Search className="mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="text-lg font-semibold">{t('tickets.select.no_results')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('tickets.select.try_different_search')}
          </p>
        </div>
      )}
    </div>
  );
}
